import { useMemo, useState, type FormEvent } from 'react';
import type { StoreId } from '@shared/types.ts';
import { newId, sentenceCase } from '@shared/text.ts';
import { getStore, useAppState } from '../../data/store.ts';
import { isPerishable, storesFor } from '@shared/perishable.ts';
import { KindToggle, Stepper, money } from '../../components/ui.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Link, back, navigate } from '../../lib/router.tsx';
import { ProductMatchForm } from '../groceries/ProductMatchForm.tsx';

export function StaplesScreen() {
  const store = getStore();
  const staples = useAppState((s) => s.staples);
  const products = useAppState((s) => s.products);
  const [name, setName] = useState('');
  const sorted = useMemo(() => [...staples].sort((a, b) => Number(b.usual) - Number(a.usual) || a.name.localeCompare(b.name)), [staples]);

  const add = (e: FormEvent) => {
    e.preventDefault();
    const n = sentenceCase(name);
    if (!n) return;
    if (store.stapleByName(n)) {
      store.toast(`${n} is already a staple`);
      return;
    }
    const id = newId('st_');
    store.upsertStaple({ id, name: n, usualQty: 1, usual: false });
    setName('');
    navigate(`/settings/staples/${id}`, { transition: 'forward' });
  };

  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/settings')}>
        <Icon name="chevron-left" size={18} /> Settings
      </button>
      <h1 className="h1 h1-sm">Staples</h1>
      <span className="small muted">Things you buy again and again. Each is matched once to a product at each store; "the usual" ones are added together.</span>
      <form className="row" style={{ gap: 8 }} onSubmit={add}>
        <label htmlFor="new-staple" className="sr-only">
          New staple
        </label>
        <input id="new-staple" className="input grow" placeholder="Add a staple" value={name} onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="icon-btn gold" aria-label="Add staple" disabled={!name.trim()}>
          <Icon name="plus" size={22} />
        </button>
      </form>
      <div className="card-flush">
        {sorted.map((s) => {
          const matches = products.filter((p) => p.stapleId === s.id);
          return (
            <Link key={s.id} to={`/settings/staples/${s.id}`} className="kv-row" style={{ color: 'var(--text)' }}>
              <span className="stack" style={{ gap: 2 }}>
                <span style={{ fontWeight: 600 }}>
                  {s.name}
                  {s.usual && (
                    <span className="badge badge-gold" style={{ marginLeft: 8, fontSize: 11 }}>
                      usual
                    </span>
                  )}
                </span>
                <span className="small muted">{matches.length ? matches.map((m) => `${store.state.stores.find((x) => x.id === m.storeId)?.name}${m.lastPrice != null ? ` ${money(m.lastPrice)}` : ''}`).join(' · ') : 'No store matches yet'}</span>
              </span>
              <Icon name="chevron-right" size={18} />
            </Link>
          );
        })}
        {sorted.length === 0 && <div className="empty" style={{ border: 'none' }}>No staples yet.</div>}
      </div>
    </div>
  );
}

export function StapleScreen({ stapleId }: { stapleId: string }) {
  const store = getStore();
  const staples = useAppState((s) => s.staples);
  const products = useAppState((s) => s.products);
  const stores = useAppState((s) => s.stores);
  const staple = staples.find((s) => s.id === stapleId);
  const [editing, setEditing] = useState<StoreId | null>(null);
  const [name, setName] = useState(staple?.name ?? '');
  if (!staple) return <div className="screen"><div className="empty">This staple was deleted.</div></div>;
  const perishable = isPerishable(staple);
  // Only stores that take this kind of item.
  const enabled = storesFor(stores.filter((s) => s.enabled), perishable);

  return (
    <div className="screen" style={{ gap: 14 }}>
      <button type="button" className="back-link" onClick={() => back('/settings/staples')}>
        <Icon name="chevron-left" size={18} /> Staples
      </button>
      <div className="card stack-lg">
        <div className="field">
          <label htmlFor="staple-name">Name</label>
          <input
            id="staple-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => name.trim() && store.upsertStaple({ ...staple, name: sentenceCase(name) })}
          />
        </div>
        <div className="row-between">
          <span style={{ fontSize: 13, fontWeight: 600 }}>Usual quantity</span>
          <Stepper value={staple.usualQty} min={1} onChange={(n) => store.upsertStaple({ ...staple, usualQty: Math.max(1, n) })} label="usual quantity" boxed />
        </div>
        <label className="row" style={{ gap: 10 }}>
          <input type="checkbox" className="checkbox" checked={staple.usual} onChange={(e) => store.upsertStaple({ ...staple, usual: e.target.checked })} />
          Part of "Add the usual"
        </label>
        <KindToggle
          perishable={perishable}
          guessed={staple.perishable == null}
          goesTo={enabled.map((s) => s.name)}
          onChange={(p) => store.upsertStaple({ ...staple, perishable: p })}
        />
      </div>

      <h2 className="section-label">Where we buy it</h2>
      {enabled.map((s) => {
        const p = products.find((x) => x.stapleId === staple.id && x.storeId === s.id);
        if (editing === s.id) {
          return (
            <div className="card" key={s.id}>
              <span style={{ fontWeight: 600, display: 'block', marginBottom: 12 }}>{s.name}</span>
              <ProductMatchForm
                store={s}
                initial={p}
                defaultTitle={staple.name}
                onCancel={() => setEditing(null)}
                onSave={(m) => {
                  store.upsertProduct({ ...p, ...m, id: p?.id ?? newId('sp_'), stapleId: staple.id, available: true, updatedAt: new Date().toISOString() });
                  setEditing(null);
                }}
              />
            </div>
          );
        }
        return (
          <div className="card row" key={s.id} style={{ padding: '12px 12px 12px 16px' }}>
            <div className="grow stack" style={{ gap: 2 }}>
              <span style={{ fontWeight: 600 }}>{s.name}</span>
              <span className="small muted">
                {p ? `${p.title}${p.lastPrice != null ? ` · ${money(p.lastPrice)}` : ''}${p.sizeLabel ? ` · ${p.sizeLabel}` : ''}` : 'Not matched'}
              </span>
            </div>
            {p && (
              <button type="button" className="icon-btn" aria-label={`Remove ${s.name} match`} onClick={() => store.deleteProduct(p.id)}>
                <Icon name="trash" size={18} />
              </button>
            )}
            <button type="button" className="btn" onClick={() => setEditing(s.id)}>
              {p ? 'Edit' : 'Match'}
            </button>
          </div>
        );
      })}

      <button
        type="button"
        className="btn btn-danger"
        onClick={() => {
          if (!confirm(`Delete ${staple.name} and its store matches?`)) return;
          store.deleteStaple(staple.id);
          navigate('/settings/staples', { replace: true, transition: 'back' });
        }}
      >
        <Icon name="trash" size={18} /> Delete staple
      </button>
    </div>
  );
}
